import { t } from 'i18next';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import GridSkeletons from '../../../components/skeletons/GridSkeletons';
import { LocationEvent } from '../../../domain/models/locations/LocationEvent';
import { SimpleMember } from '../../../domain/models/locations/SimpleMember';
import { ReactComponent as LeaderIcon } from '../../../icons/leader.svg';
import { ILocationEventsResponseBody } from '../../../types/response/ILocationEventsResponseBody';
import dDayCalculate from '@pocapan/shared/utils/dDayCalculate';
import { getSiteType } from '@pocapan/shared/utils/SiteTypes';

interface RelatedEventsProps {
  relatedEvents: ILocationEventsResponseBody[];
  eventDetail: LocationEvent;
}

const RelatedEvents: React.FC<RelatedEventsProps> = ({ relatedEvents, eventDetail }) => {
  const navigate = useNavigate();

  const memberNames = eventDetail.members.map((member: SimpleMember) => member.name).join(' ');
  const filteredEvents = React.useMemo(() => {
    if (!relatedEvents || !eventDetail) {
      return [];
    }

    return relatedEvents.filter((event) => event.eventId !== eventDetail?.eventId);
  }, [relatedEvents, eventDetail]);

  const naviateEventDetailPage = (locationId: string, eventId: string) => {
    navigate(`/locations/${locationId}/events/${eventId}`);
  };

  if (!eventDetail && !relatedEvents) {
    return (
      <GridSkeletons
        className={'px-[20px]'}
        gridSize={1}
        skeletonSize={1}
        skeletonHeight={'260px'}
      />
    );
  }

  return (
    <>
      <div className="text-[15px] leading-[18px] font-semibold text-main-light-2 dark:text-main-dark-2 pl-[20px]">
        {eventDetail.artist.shortName} {memberNames}
        {t('OfflineEventDetail.otherEvent')}
      </div>
      <div className="mt-[16px] overflow-x-auto hidden-scroll pl-[20px]">
        <div className="flex flex-nowrap mb-[4px] gap-[10px]">
          {filteredEvents?.map((event) => {
            const dDay = dDayCalculate(event.startDate, event.endDate);
            return (
              <div
                key={event.eventId}
                className="w-[124px] cursor-pointer flex-shrink-0"
                onClick={() => naviateEventDetailPage(event.locationId, event.eventId)}
              >
                <div className="relative">
                  <img
                    src={event?.baseImageUrl}
                    alt="event_image"
                    className="rounded-[10px] w-[124px] h-[147px] object-cover"
                  />
                  {new Date(event.endDate) < new Date() ? (
                    <>
                      <span className="absolute top-0 rounded-xl w-[7.75rem] h-[9.188rem] bg-black/30" />
                      <span
                        className="absolute text-pure-white"
                        style={{
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)'
                        }}
                      >
                        {t('OfflineEventDetail.eventEnd')}
                      </span>
                    </>
                  ) : (
                    <span className="absolute top-0 left-0 content-center items-center justify-center w-fit h-[23px] rounded-tl-[10px] rounded-br-[10px] bg-onLabel-light text-pure-white text-[11px] font-semibold leading-[13px] py-[5px] pl-[9px] pr-[8px]">
                      {dDay}
                    </span>
                  )}
                  {/* TODO: Show Alert Status */}
                  {/* <img src={alarm_icon} alt="alarm" className="absolute right-3 bottom-3" /> */}
                </div>

                <div className="flex flex-col gap-[6px]">
                  <div className="flex flex-row gap-[4px] mt-[8px] text-[13px] items-center text-main-light-3 dark:text-main-dark-3">
                    <img
                      src={`/assets/icons/graphic-3d/${getSiteType(event.category)?.iconName}.png`}
                      alt="site_type_icon"
                      className="w-[13px] h-[13px] inline-block"
                    />
                    <span>
                      {event.members && event.members.length > 0
                        ? `${event.artist.shortName} ${event.members
                            .map((member) => member.name)
                            .join(', ')}`
                        : event.artist.name}
                    </span>
                  </div>

                  <div className="text-[16px] leading-[19px] font-semibold text-main-light-2 dark:text-main-dark-2 overflow-hidden [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
                    {event?.name}
                  </div>

                  <div className="flex flex-row text-[13px] font-normal gap-[4px] items-center text-main-light-4 dark:text-main-dark-4">
                    <LeaderIcon className="w-[14px] h-[14px] flex-shrink-0 fill-sub-light-1 dark:fill-sub-dark-1" />
                    <div className="leading-[16px] truncate">{event?.organizerTwitterId}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default RelatedEvents;
